import { CheckHeaderState } from "components/table/checkHeader/checkHeaderState";
import { isCheckBoxDefinition, CheckboxDefinition } from "components/table/columns/CheckColumn";
import { TableModel } from "components/table/tableModel";

export class TableStatus {

    // Whenever are changes in the data (only for inline editing)
    public changed: boolean;
    // We want to store here the rowKey and the index of the data in the model.
    private checked: CheckedModel = {};
    private checkHeaderStatus: CheckHeaderStatusModel = {};
    private invalidRows = {};
    private modifiedRows = new Set();

    private newRows: any[][] = [];
    private tableModel: TableModel;
    public isRendered: boolean = false;
    public isCreated: boolean = false;
    private tableType: TableType;
    public addedRowsValid: boolean = true;
    public disabled = false;
    public disabledColumns;

    constructor(tableModel: TableModel) {
        this.tableModel = tableModel;
    }

    public setValid(rowKey: string, rowindex: number, isValid: boolean): void {
        let key = rowKey || rowindex;
        if (isValid) {
            delete this.invalidRows[key];
        } else {
            this.invalidRows[key] = rowindex;
        }
    }

    public isValid() {
        return Object.keys(this.invalidRows).length === 0
            && this.invalidRows.constructor === Object
            && this.addedRowsValid;
    }

    public initCheckedProperty(property: string): void {
        this.checked[property] = {};
    }

    /**
     * Returns an array with the keys of the checked rows.
     * @param property column property
     */
    public checkedRows(property: string): { [rowKey: string]: any[] } {
        return this.checked[property];
    }

    public isAnyChecked(property: string): boolean {
        return (this.checked[property] != null
            && Object.keys(this.checked[property]).length > 0)
            || this.checkHeaderStatus[property]?.status === "all-checked";
    }

    public tracksUnchecked(property: string) {
        return (this.checkHeaderStatus[property]?.status === "some-unchecked"
            || this.checkHeaderStatus[property]?.status === "all-checked")
            && this.isServerType();
    }

    public setChecked(
        columnProperty: string,
        rowKey: string,
        data: any[],
        isChecked: boolean) {

        if (this.checked[columnProperty] == null) {
            this.checked[columnProperty] = {};
        }
        if (this.checkHeaderStatus[columnProperty] == null) {
            this.checkHeaderStatus[columnProperty] = new CheckHeaderState();
        }

        const tracksUnchecked = this.tracksUnchecked(columnProperty);
        if ((isChecked && !tracksUnchecked) || (!isChecked && tracksUnchecked)) {
            this.checked[columnProperty][rowKey] = data;
        } else {
            delete this.checked[columnProperty][rowKey];
        }
    }

    public calculateCheckHeaderStatus(columnProperty: string, columnIndex: number): CheckHeaderState {

        let checkedCount = Object.keys(this.checked[columnProperty]).length;
        if (checkedCount === 0) {
            this.checkHeaderStatus[columnProperty].uncheckLastRow();
        } else if (this.isClientType()) {
            let checkableCount = this.tableModel.getSelectableRowsCount(columnIndex);
            if (checkedCount === checkableCount) {
                this.checkHeaderStatus[columnProperty].checkRowUpToAll();
            } else {
                this.checkHeaderStatus[columnProperty].clickRow(this.isClientType());
            }
        } else {
            this.checkHeaderStatus[columnProperty].clickRow(this.isClientType());
        }

        return this.checkHeaderStatus[columnProperty];
    }


    public isChecked(property: string, rowKey: string): boolean {
        if (this.checkHeaderStatus[property]?.status === "all-checked") {
            return true;
        }
        if (this.checkHeaderStatus[property]?.status === "some-unchecked") {
            return (this.checked[property][rowKey] == null);
        }
        if (this.checked[property] != null) {
            return (this.checked[property][rowKey] != null);
        }
        return false;
    }

    public clickHeaderCheckbox(property: string, columnIndex: number, rowKey): CheckHeaderState {

        if (this.checkHeaderStatus[property] == null) {
            this.checkHeaderStatus[property] = new CheckHeaderState();
        }
        this.checkHeaderStatus[property].clickHeader();
        if (this.checkHeaderStatus[property].status === "all-checked") {
            if (this.isClientType()) {
                this.checked[property] = this.checked[property] ?? {};
                this.tableModel.getColumn(columnIndex).forEach((cellDataModel, i) => {
                    //Si es boolean o CheckBoxDefinition es una celda de checkbox
                    if (cellDataModel || isCheckBoxDefinition(cellDataModel)) {
                        let rowDataModel = this.tableModel.getRow(i);
                        let key = rowKey.key(rowDataModel);
                        this.checked[property][key] = rowDataModel;
                    }
                });
            } else {
                this.checked[property] = {};
            }
        } else {
            this.tableModel.getColumn(columnIndex).forEach((cellDataModel, i) => {
                if (cellDataModel || isCheckBoxDefinition(cellDataModel)) {
                    let rowDataModel = this.tableModel.getRow(i);
                    let key = rowKey.key(rowDataModel);
                    delete this.checked[property][key];
                }
            });
        }

        if (this.checkHeaderStatus[property].status === "none-checked") {
            this.checked[property] = {};
        }
        // console.log(this.checked, this.checkHeaderStatus);
        return this.checkHeaderStatus[property];
    }

    public getModifiedRows(): any[] {
        return Array.from(this.modifiedRows);
    }

    public addModifiedRow(arg0: any): void {
        this.modifiedRows.add(arg0);
    }

    public get addedRows() {
        return this.newRows;
    }

    public set addedRows(newRows) {
        this.newRows = newRows;;
    }

    public reset(): any {
        this.changed = false;
        this.checkHeaderStatus = {};
        this.checked = {};
        this.modifiedRows = new Set();
        this.newRows = [];
    }

    public isClientType() {
        return this.tableType === TableType.client;
    }

    public isServerType() {
        return this.tableType === TableType.server;
    }

    public setClientType(): boolean {
        let changed = this.tableType !== TableType.client;
        this.tableType = TableType.client;
        return changed;
    }

    public setServerType(): boolean {
        let changed = this.tableType !== TableType.server;
        this.tableType = TableType.server;
        return changed;
    }

    public get checkedInfo(): CheckedInfo {
        let checkedInfo = {};
        for (const key in this.checked) {
            if (this.checked.hasOwnProperty(key)) {
                checkedInfo[key] = {};
                checkedInfo[key].checked = this.checked[key];
                checkedInfo[key].property = key;
                checkedInfo[key].isComplete = this.isClientType();
            }
        }
        for (const key in this.checkHeaderStatus) {
            if (this.checkHeaderStatus.hasOwnProperty(key)) {
                checkedInfo[key] = { ...(checkedInfo[key] ?? {}), state: this.checkHeaderStatus[key] };
            }
        }

        return checkedInfo;
    }

    public isDisabled(column: string): boolean {
        return this.disabled || this.disabledColumns["column"];
    }
}

interface CheckedModel {
    [columnProperty: string]: { [rowKey: string]: any[] };
}

interface CheckHeaderStatusModel {
    [columnProperty: string]: CheckHeaderState;
}

export enum TableType {
    server, client,
}

export interface CheckedInfo {
    [columnProperty: string]: CheckedPropertyInfo;
}

/**
 * DTO for the checked info web component.
 */
export interface CheckedPropertyInfo {
    property: string;
    state: CheckHeaderState;
    checked: CheckedModel;
    isComplete: boolean;
}
